import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getUserProfile, updateUserProfile, uploadProfilePhoto } from '@/lib/db';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Camera, Loader2, User } from 'lucide-react';
import type { UserProfile } from '@/types/user';
import type { HDType } from '@/lib/types';

const MAX_FILE_SIZE = 500 * 1024; // 500ko en bytes
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const HD_TYPES = [
  { value: 'projector', label: 'Projector' },
  { value: 'generator', label: 'Generator' },
  { value: 'manifestor', label: 'Manifestor' },
  { value: 'manifesting-generator', label: 'Manifesting Generator' },
  { value: 'reflector', label: 'Reflector' }
];

export default function Profile() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  useEffect(() => {
    if (!currentUser) {
      navigate('/barometre/login');
      return;
    }

    const loadProfile = async () => {
      try {
        if (!currentUser.id) {
          throw new Error('User ID not found');
        }

        const userProfile = await getUserProfile(currentUser.id);
        if (!userProfile) {
          toast({
            variant: "destructive",
            title: "Erreur",
            description: "Profil utilisateur non trouvé.",
          });
          return;
        }
        setProfile(userProfile);
        if (userProfile.photoUrl) {
          setPhotoPreview(userProfile.photoUrl);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger le profil.",
        });
        navigate('/barometre/dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [currentUser, navigate, toast]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      toast({
        variant: "destructive",
        title: "Format non supporté",
        description: "Veuillez choisir une image au format JPG, PNG ou WebP.",
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast({
        variant: "destructive",
        title: "Fichier trop volumineux",
        description: "La taille de l'image ne doit pas dépasser 500 Ko.",
      });
      return;
    }

    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateProfile = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!currentUser?.id || !profile) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Session invalide. Veuillez vous reconnecter.",
      });
      navigate('/barometre/login');
      return;
    }

    setUpdating(true);
    try {
      const formData = new FormData(e.currentTarget);
      const updates: Partial<UserProfile> = {
        displayName: formData.get('displayName') as string,
        energyType: formData.get('energyType') as HDType,
      };

      if (photoFile) {
        try {
          const photoUrl = await uploadProfilePhoto(currentUser.id, photoFile);
          updates.photoUrl = photoUrl;
        } catch (error) {
          console.error('Error uploading photo:', error);
          toast({
            variant: "destructive",
            title: "Erreur",
            description: "Impossible d'uploader la photo.",
          });
          return;
        }
      }

      await updateUserProfile(currentUser.id, updates);
      setProfile(prev => prev ? { ...prev, ...updates } : null);
      
      toast({
        title: "Profil mis à jour",
        description: "Vos modifications ont été enregistrées.",
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de mettre à jour le profil.",
      });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-2xl mx-auto">
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader>
            <h1 className="text-2xl font-bold">Mon Profil</h1>
            <p className="text-gray-600">Gérez vos informations personnelles</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-6">
              {/* Photo de profil */}
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-lg">
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Photo de profil"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <Camera className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <label
                    htmlFor="photo"
                    className="absolute bottom-0 right-0 bg-purple-600 text-white p-2 rounded-full cursor-pointer hover:bg-purple-700 transition-colors"
                  >
                    <Camera className="w-4 h-4" />
                  </label>
                  <input
                    type="file"
                    id="photo"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                </div>
                <p className="text-sm text-gray-500">
                  JPG, PNG ou WebP. 500 Ko maximum.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={profile?.email || ''}
                    disabled
                    className="w-full h-12 px-4 rounded-lg border border-gray-300 bg-gray-50 text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom d'utilisateur
                  </label>
                  <input
                    type="text"
                    name="displayName"
                    defaultValue={profile?.displayName || ''}
                    className="w-full h-12 px-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Votre nom d'utilisateur"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type d'énergie
                  </label>
                  <select
                    name="energyType"
                    defaultValue={profile?.energyType}
                    className="w-full h-12 px-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {HD_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-[#54075B] hover:bg-[#54075B]/90 flex items-center justify-center gap-2"
                disabled={updating}
              >
                {updating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Mise à jour...
                  </>
                ) : (
                  'Mettre à jour le profil'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}